import { defineStore } from "pinia";
import { z } from "zod";
import { useMjmlStore } from "./mjmlStore";
import { useNotificationsStore } from "./notifications";
import { useMergeTagsStore } from "./mergeTags";
import {
  TopolVariantConditionSchema,
  TopolSectionVariantVariantSchema,
  TopolSectionVariantSchema,
  TopolVariatOtherConditionSchema,
} from "@/schemas/templateSchema";
import { i18n } from "@/init/i18n-setup";

type TopolVariantCondition = z.output<typeof TopolVariantConditionSchema>;
type TopolVariant = z.output<typeof TopolSectionVariantVariantSchema>;
type TopolSectionVariants = z.output<typeof TopolSectionVariantSchema>;
type TopolVariatOtherCondition = z.output<
  typeof TopolVariatOtherConditionSchema
>;
const keys = TopolVariatOtherConditionSchema.keyof();

type TopolVariantConditionKeys = z.output<typeof keys>;

interface State {
  enableSectionVariants?: boolean;
  logicalOperatorsInsideVariant: boolean;
  openVariantPanel: boolean;
  disableExtendedComparators?: boolean;
}

export const useSectionVariantsStore = defineStore("sectionVariants", {
  state: (): State => ({
    enableSectionVariants: false,
    logicalOperatorsInsideVariant: false,
    openVariantPanel: false,
  }),

  getters: {
    getSectionVariants:
      () =>
      (id: string): TopolVariant[] => {
        const mjmlStore = useMjmlStore();
        const thisSectionVariants = mjmlStore.mjml.sectionVariants?.find(
          (item) => item.sections.includes(id)
        );

        if (!thisSectionVariants) return [];

        if (
          !thisSectionVariants.variants.find(
            (item: TopolVariant) => item[id]
          ) &&
          thisSectionVariants.elseVariantUid !== id
        ) {
          thisSectionVariants.sections.splice(
            thisSectionVariants.sections.indexOf(id),
            1
          );
          if (thisSectionVariants.activeVariant === id) {
            thisSectionVariants.activeVariant = thisSectionVariants.sections[0];
          }
          return [];
        }

        return thisSectionVariants.variants;
      },

    getActiveVariantUid:
      () =>
      (id: string): string => {
        const mjmlStore = useMjmlStore();
        const thisSectionVariants = mjmlStore.mjml.sectionVariants?.find(
          (item) => item.sections.includes(id)
        );

        if (thisSectionVariants) {
          if (
            !thisSectionVariants.activeVariant ||
            thisSectionVariants.activeVariant === ""
          ) {
            thisSectionVariants.activeVariant = thisSectionVariants.sections[0];
          }
          return thisSectionVariants.activeVariant;
        }

        return "";
      },

    getElseVariant:
      () =>
      (id: string): string | undefined => {
        const mjmlStore = useMjmlStore();
        const thisSectionVariants = mjmlStore.mjml.sectionVariants?.find(
          (item) => item.sections.includes(id)
        );

        if (thisSectionVariants) {
          return thisSectionVariants.elseVariantUid;
        }
      },

    getVariantName:
      () =>
      (id: string): any => {
        const mjmlStore = useMjmlStore();
        const thisSectionVariants = mjmlStore.mjml.sectionVariants?.find(
          (item) => item.sections.includes(id)
        );
        const thisVariant = thisSectionVariants?.variants.find(
          (item: TopolVariant) => item[id]
        );

        if (thisVariant && thisVariant[id]) {
          return thisVariant[id].name;
        }
      },

    getVariantConditionProperty:
      () =>
      (
        id: string,
        prop:
          | TopolVariantConditionKeys
          | "conditionsSeparator"
          | "otherConditions"
      ): any => {
        const mjmlStore = useMjmlStore();
        const thisSectionVariants = mjmlStore.mjml.sectionVariants?.find(
          (item) => item.sections.includes(id)
        );
        const thisVariant = thisSectionVariants?.variants.find(
          (item: TopolVariant) => item[id]
        );

        if (thisVariant && thisVariant[id]) {
          return thisVariant[id].condition[prop];
        }
      },
  },

  actions: {
    allConditionsFilled(
      conditions: TopolVariatOtherCondition[] | undefined
    ): boolean {
      let allFilled = true;

      if (!conditions) return false;

      conditions.forEach((condition) => {
        if (
          condition.conditionField === "" ||
          (condition.conditionComparator !== "exist" &&
            condition.conditionValue === "")
        ) {
          allFilled = false;
        }
      });

      return allFilled;
    },
    addOtherCondition(id: string) {
      const thisSectionVariants = this.getThisVariant(id);
      const thisVariant = thisSectionVariants?.variants.find(
        (item: TopolVariant) => item[id]
      );

      if (!thisSectionVariants || !thisVariant || !thisVariant[id]) return;

      const condition = thisVariant[id].condition;

      if (!condition.conditionsSeparator) condition.conditionsSeparator = "AND";

      if (!condition.otherConditions) condition.otherConditions = [];

      condition.otherConditions.push({
        conditionField: "",
        conditionComparator: "is",
        conditionValue: "",
      });
    },
    addSectionElseVariant(id: string) {
      const thisSectionVariants = this.getThisVariant(id);

      if (thisSectionVariants) {
        const mjmlStore = useMjmlStore();
        mjmlStore.takeSnapshot();
        const upUid =
          thisSectionVariants.sections[thisSectionVariants.sections.length - 1];
        const upVariant = thisSectionVariants.variants.find(
          (item: TopolVariant) => item[upUid]
        );
        const sectionIndex = mjmlStore.getSectionIndexByUid(upUid);

        if (upVariant && upVariant[upUid].condition.conditionField === "") {
          const notificationsStore = useNotificationsStore();
          notificationsStore.createNotification({
            type: "error",
            text: i18n.global.t("variants.condition_must_be_filled"),
          });
          return;
        }

        mjmlStore.duplicateSection({
          sectionIndex: sectionIndex,
          inVariant: true,
        });

        mjmlStore.selectSection({
          sectionIndex: sectionIndex + 1,
        });

        const newSectionId = mjmlStore.getSelectedSectionByIndex(
          sectionIndex + 1
        ).uid;

        thisSectionVariants.sections.push(newSectionId);
        thisSectionVariants.elseVariantUid = newSectionId;
        thisSectionVariants.activeVariant = newSectionId;

        this.updateElseCondition(thisSectionVariants);
      }
    },
    addSectionVariant(id: string) {
      const mjmlStore = useMjmlStore();
      mjmlStore.takeSnapshot();
      const thisSectionVariants = this.getThisVariant(id);

      if (!mjmlStore.mjml.sectionVariants) {
        const variant: TopolVariant = {
          [id]: {
            condition: {
              conditionField: "",
              conditionComparator: "is",
              conditionValue: "",
            },
            name: "",
          },
        };

        mjmlStore.mjml.sectionVariants = [];

        mjmlStore.mjml.sectionVariants.push({
          sections: [id],
          variants: [variant],
          activeVariant: id,
          elseCondition: "",
        });
      } else if (thisSectionVariants) {
        const upUid =
          thisSectionVariants.sections[
            thisSectionVariants.sections.length -
              (thisSectionVariants.elseVariantUid ? 2 : 1)
          ];
        const upVariant = thisSectionVariants.variants.find(
          (item: TopolVariant) => item[upUid]
        );
        const sectionIndex = mjmlStore.getSectionIndexByUid(upUid);

        if (
          upVariant &&
          (upVariant[upUid].condition.conditionField === "" ||
            (upVariant[upUid].condition.conditionsSeparator &&
              !this.allConditionsFilled(
                upVariant[upUid].condition.otherConditions
              )))
        ) {
          const notificationsStore = useNotificationsStore();
          notificationsStore.createNotification({
            type: "error",
            text: i18n.global.t("variants.condition_must_be_filled"),
          });
          return;
        }

        mjmlStore.duplicateSection({
          sectionIndex: sectionIndex,
          inVariant: true,
        });

        mjmlStore.selectSection({
          sectionIndex: sectionIndex + 1,
        });

        const newSectionId = mjmlStore.getSelectedSectionByIndex(
          sectionIndex + 1
        ).uid;

        const variant: TopolVariant = {
          [newSectionId]: {
            condition: {
              conditionField:
                upVariant &&
                upVariant[upUid].condition.conditionComparator !== ""
                  ? upVariant[upUid].condition.conditionField
                  : "",
              conditionComparator: "is",
              conditionValue: "",
            },
            name: "",
          },
        };

        if (thisSectionVariants.elseVariantUid) {
          thisSectionVariants.sections.splice(
            thisSectionVariants.sections.length - 1,
            0,
            newSectionId
          );
        } else {
          thisSectionVariants.sections.push(newSectionId);
        }
        thisSectionVariants.variants.push(variant);
        thisSectionVariants.activeVariant = newSectionId;
      } else {
        const variant: TopolVariant = {
          [id]: {
            condition: {
              conditionField: "",
              conditionComparator: "is",
              conditionValue: "",
            },
            name: "",
          },
        };

        mjmlStore.mjml.sectionVariants.push({
          sections: [id],
          variants: [variant],
          activeVariant: id,
          elseCondition: "",
        });
      }
    },
    addSectionToElseVariants(currentId: string, elseId: string) {
      const mjmlStore = useMjmlStore();
      mjmlStore.takeSnapshot();
      const thisSectionVariants = mjmlStore.mjml.sectionVariants?.find((item) =>
        item.sections.includes(elseId)
      );
      if (thisSectionVariants) {
        const upUid =
          thisSectionVariants.sections[
            thisSectionVariants.sections.length -
              (thisSectionVariants.elseVariantUid ? 2 : 1)
          ];
        const upVariant = thisSectionVariants.variants.find(
          (item: TopolVariant) => item[upUid]
        );
        const currentSectionIndex = mjmlStore.getSectionIndexByUid(currentId);
        const upSectionIndex = mjmlStore.getSectionIndexByUid(upUid);

        if (upVariant && upVariant[upUid].condition.conditionField === "") {
          const notificationsStore = useNotificationsStore();
          notificationsStore.createNotification({
            type: "error",
            text: i18n.global.t("variants.condition_must_be_filled"),
          });
          return;
        }

        mjmlStore.reorderSections({
          oldIndex: currentSectionIndex,
          newIndex:
            upSectionIndex + (currentSectionIndex > upSectionIndex ? 1 : 0),
          inVariants: true,
        });

        mjmlStore.selectSection({
          sectionIndex:
            upSectionIndex + (currentSectionIndex > upSectionIndex ? 1 : 0),
        });

        const variant: TopolVariant = {
          [currentId]: {
            condition: {
              conditionField:
                upVariant &&
                upVariant[upUid].condition.conditionComparator !== ""
                  ? upVariant[upUid].condition.conditionField
                  : "",
              conditionComparator: "is",
              conditionValue: "",
            },
            name: "",
          },
        };

        if (thisSectionVariants.elseVariantUid) {
          thisSectionVariants.sections.splice(
            thisSectionVariants.sections.length - 1,
            0,
            currentId
          );
        } else {
          thisSectionVariants.sections.push(currentId);
        }
        thisSectionVariants.variants.push(variant);
        thisSectionVariants.activeVariant = currentId;
      }
    },
    activateVariant(id: string) {
      const mjmlStore = useMjmlStore();
      const mergeTagsStore = useMergeTagsStore();
      const sectionIndex = mjmlStore.getSectionIndexByUid(id);
      const thisSectionVariants = this.getThisVariant(id);

      if (thisSectionVariants) {
        thisSectionVariants.activeVariant = id;

        // If this is a language variant, switch the editor language too
        const thisVariant = thisSectionVariants.variants.find(
          (item: TopolVariant) => item[id]
        );
        if (
          thisVariant?.[id]?.condition.conditionField ===
          mergeTagsStore.languageMergeTagName
        ) {
          const langCode = thisVariant[id].condition.conditionValue;
          if (
            langCode &&
            mjmlStore.mjml.langs?.find((l) => l.key === langCode)
          ) {
            mjmlStore.langToShow = langCode;
          }
        } else if (
          thisSectionVariants.elseVariantUid === id &&
          thisSectionVariants.variants.some((item: TopolVariant) => {
            const key = Object.keys(item)[0];
            return (
              item[key]?.condition.conditionField ===
              mergeTagsStore.languageMergeTagName
            );
          })
        ) {
          // Else variant in a language variant group — switch to primary language
          const primaryLang = mjmlStore.mjml.langs?.find((l) => l.primary);
          if (primaryLang) {
            mjmlStore.langToShow = primaryLang.key;
          }
        }
      }

      mjmlStore.selectSection({
        sectionIndex: sectionIndex,
      });
    },
    conditionBiulder(condition: TopolVariantCondition): string {
      let conditionString = "";
      if (condition.conditionField !== "") {
        conditionString = condition.conditionField;
        if (condition.conditionComparator === "is") {
          conditionString += "=" + condition.conditionValue;
        } else if (condition.conditionComparator === "is not") {
          conditionString += "!=" + condition.conditionValue;
        } else if (condition.conditionComparator !== "exist") {
          conditionString += condition.conditionComparator;
          conditionString += condition.conditionValue;
        }

        if (condition.conditionsSeparator && condition.otherConditions) {
          condition.otherConditions.forEach((item) => {
            conditionString += ":" + condition.conditionsSeparator + ":";
            conditionString += item.conditionField;
            if (item.conditionComparator === "is") {
              conditionString += "=" + item.conditionValue;
            } else if (item.conditionComparator === "is not") {
              conditionString += "!=" + item.conditionValue;
            } else if (item.conditionComparator !== "exist") {
              conditionString += item.conditionComparator;
              conditionString += item.conditionValue;
            }
          });
        }
      }

      return conditionString;
    },
    getThisVariant(id: string): TopolSectionVariants | undefined {
      const mjmlStore = useMjmlStore();
      return mjmlStore.mjml.sectionVariants?.find((item) =>
        item.sections.includes(id)
      );
    },

    hasDupliciteCondition(id: string) {
      const thisSectionVariants = this.getThisVariant(id);
      const thisVariant = thisSectionVariants?.variants.find(
        (item: TopolVariant) => item[id]
      );
      let sameCondition = false;

      if (thisSectionVariants && thisVariant && thisVariant[id]) {
        const condition = this.conditionBiulder(thisVariant[id].condition);

        thisSectionVariants.variants.map(
          (varinat: TopolVariant, index: number) => {
            if (varinat[id]) {
              return;
            }

            if (
              this.conditionBiulder(
                varinat[thisSectionVariants.sections[index]].condition
              ) === condition
            ) {
              sameCondition = true;
            }
          }
        );
      }
      return sameCondition;
    },

    openSecntionVariants(id: string) {
      this.openVariantPanel = true;
      const mjmlStore = useMjmlStore();
      const thisSectionVariants = this.getThisVariant(id);

      if (!(mjmlStore.mjml.sectionVariants && thisSectionVariants)) {
        this.addSectionVariant(id);
      }
    },
    removeElseVariant(id: string) {
      const thisSectionVariants = this.getThisVariant(id);
      const elseVariantUid = thisSectionVariants?.elseVariantUid;

      if (thisSectionVariants && elseVariantUid) {
        const mjmlStore = useMjmlStore();
        mjmlStore.takeSnapshot();
        const elseSectionIndex = mjmlStore.getSectionIndexByUid(elseVariantUid);
        const indexOfVariant =
          thisSectionVariants.sections.indexOf(elseVariantUid);

        if (thisSectionVariants.activeVariant === elseVariantUid) {
          thisSectionVariants.activeVariant =
            thisSectionVariants.sections[indexOfVariant - 1];
        }

        const selectSectionIndex = mjmlStore.getSectionIndexByUid(
          thisSectionVariants.activeVariant
        );

        mjmlStore.selectSection({
          sectionIndex: selectSectionIndex,
        });
        thisSectionVariants.sections.splice(indexOfVariant, 1);
        thisSectionVariants.elseVariantUid = undefined;
        mjmlStore.deleteSection({
          sectionIndex: elseSectionIndex,
          withoutVariantCheck: true,
        });
      }
    },
    removeVariant(id: string) {
      const mjmlStore = useMjmlStore();
      const sectionIndex = mjmlStore.getSectionIndexByUid(id);
      const thisSectionVariants = this.getThisVariant(id);

      if (mjmlStore.mjml.sectionVariants && thisSectionVariants) {
        mjmlStore.takeSnapshot();
        const indexOfVariant = thisSectionVariants.sections.indexOf(id);

        if (thisSectionVariants.variants.length > 1) {
          if (thisSectionVariants.activeVariant === id && indexOfVariant > 0) {
            thisSectionVariants.activeVariant =
              thisSectionVariants.sections[indexOfVariant - 1];
          } else if (thisSectionVariants.activeVariant === id) {
            thisSectionVariants.activeVariant =
              thisSectionVariants.sections[indexOfVariant + 1];
          }
          thisSectionVariants.sections.splice(indexOfVariant, 1);
          thisSectionVariants.variants.splice(indexOfVariant, 1);

          mjmlStore.deleteSection({
            sectionIndex: sectionIndex,
            withoutVariantCheck: true,
          });

          const selectSectionIndex = mjmlStore.getSectionIndexByUid(
            thisSectionVariants.activeVariant
          );

          mjmlStore.selectSection({
            sectionIndex: selectSectionIndex,
          });
        } else {
          if (
            thisSectionVariants.elseVariantUid &&
            thisSectionVariants.elseVariantUid === id
          ) {
            // Removing the else variant itself - delegate to removeElseVariant
            this.removeElseVariant(id);
            return;
          }

          if (thisSectionVariants.elseVariantUid) {
            // Keep the else section, remove the condition section
            const elseUid = thisSectionVariants.elseVariantUid;

            mjmlStore.deleteSection({
              sectionIndex: sectionIndex,
              withoutVariantCheck: true,
            });

            const elseSectionIndex = mjmlStore.getSectionIndexByUid(elseUid);
            mjmlStore.selectSection({
              sectionIndex: elseSectionIndex,
            });

            mjmlStore.updateSectionAttribute({
              prop: "css-class",
              value: null,
            });

            mjmlStore.mjml.sectionVariants.splice(
              mjmlStore.mjml.sectionVariants.indexOf(thisSectionVariants),
              1
            );
          } else {
            mjmlStore.selectSection({
              sectionIndex: sectionIndex,
            });

            mjmlStore.updateSectionAttribute({
              prop: "css-class",
              value: null,
            });

            mjmlStore.mjml.sectionVariants.splice(
              mjmlStore.mjml.sectionVariants.indexOf(thisSectionVariants),
              1
            );
          }
        }
      }
    },
    deleteVariant(id: string) {
      const mjmlStore = useMjmlStore();
      const thisSectionVariants = this.getThisVariant(id);
      const elseVariantUid = thisSectionVariants?.elseVariantUid;
      if (mjmlStore.mjml.sectionVariants && thisSectionVariants) {
        if (elseVariantUid === id) {
          const indexOfVariant =
            thisSectionVariants.sections.indexOf(elseVariantUid);

          if (thisSectionVariants.activeVariant === elseVariantUid) {
            thisSectionVariants.activeVariant =
              thisSectionVariants.sections[indexOfVariant - 1];
          }

          thisSectionVariants.sections.splice(indexOfVariant, 1);
          thisSectionVariants.elseVariantUid = undefined;
          return;
        }

        const indexOfVariant = thisSectionVariants.sections.indexOf(id);

        if (thisSectionVariants.variants.length > 1) {
          if (thisSectionVariants.activeVariant === id) {
            thisSectionVariants.activeVariant =
              thisSectionVariants.sections[indexOfVariant - 1];
          }
          thisSectionVariants.sections.splice(indexOfVariant, 1);
          thisSectionVariants.variants.splice(indexOfVariant, 1);

          this.updateElseCondition(thisSectionVariants);
        } else {
          if (thisSectionVariants.elseVariantUid) {
            // Keep the else section, remove the condition variant
            const elseUid = thisSectionVariants.elseVariantUid;

            thisSectionVariants.sections.splice(indexOfVariant, 1);
            thisSectionVariants.variants.splice(indexOfVariant, 1);
            thisSectionVariants.elseVariantUid = undefined;
            thisSectionVariants.activeVariant = elseUid;

            const elseSectionIndex = mjmlStore.getSectionIndexByUid(elseUid);
            mjmlStore.selectSection({
              sectionIndex: elseSectionIndex,
            });

            mjmlStore.updateSectionAttribute({
              prop: "css-class",
              value: null,
            });

            mjmlStore.mjml.sectionVariants.splice(
              mjmlStore.mjml.sectionVariants.indexOf(thisSectionVariants),
              1
            );
          } else {
            mjmlStore.updateSectionAttribute({
              prop: "css-class",
              value: null,
            });

            mjmlStore.mjml.sectionVariants.splice(
              mjmlStore.mjml.sectionVariants.indexOf(thisSectionVariants),
              1
            );
          }
        }
      }
    },
    removeOtherCondition(id: string, index: number) {
      const thisSectionVariants = this.getThisVariant(id);
      const thisVariant = thisSectionVariants?.variants.find(
        (item: TopolVariant) => item[id]
      );

      if (!thisSectionVariants || !thisVariant || !thisVariant[id]) return;

      const condition = thisVariant[id].condition;

      if (!condition.otherConditions) return;

      condition.otherConditions.splice(index, 1);

      const mjmlStore = useMjmlStore();
      mjmlStore.takeSnapshot();

      this.updateElseCondition(thisSectionVariants);
    },
    reorderVariants(props: any) {
      const thisSectionVariants = this.getThisVariant(props.id);

      if (thisSectionVariants) {
        const mjmlStore = useMjmlStore();
        mjmlStore.takeSnapshot();
        const sectionUid = thisSectionVariants.sections[props.oldIndex];
        const sectionIndex = mjmlStore.getSectionIndexByUid(sectionUid);

        thisSectionVariants.sections.splice(
          props.newIndex,
          0,
          thisSectionVariants.sections.splice(props.oldIndex, 1)[0]
        );

        thisSectionVariants.variants.splice(
          props.newIndex,
          0,
          thisSectionVariants.variants.splice(props.oldIndex, 1)[0]
        );

        mjmlStore.reorderSections({
          sectionIndex: sectionIndex + (props.newIndex - props.oldIndex),
          oldIndex: sectionIndex,
          newIndex: sectionIndex + (props.newIndex - props.oldIndex),
          inVariants: true,
        });
        thisSectionVariants.activeVariant = sectionUid;
      }
    },
    addLanguageVariant(id: string, langCode: string, langName: string) {
      const mjmlStore = useMjmlStore();
      const mergeTagsStore = useMergeTagsStore();
      mjmlStore.takeSnapshot();
      const thisSectionVariants = this.getThisVariant(id);

      const condition: TopolVariantCondition = {
        conditionField: mergeTagsStore.languageMergeTagName,
        conditionComparator: "is",
        conditionValue: langCode,
      };

      if (!mjmlStore.mjml.sectionVariants) {
        mjmlStore.mjml.sectionVariants = [];

        mjmlStore.mjml.sectionVariants.push({
          sections: [id],
          variants: [
            {
              [id]: {
                condition,
                name: langName,
              },
            },
          ],
          activeVariant: id,
          elseCondition: "",
        });
      } else if (thisSectionVariants) {
        const upUid =
          thisSectionVariants.sections[
            thisSectionVariants.sections.length -
              (thisSectionVariants.elseVariantUid ? 2 : 1)
          ];
        const sectionIndex = mjmlStore.getSectionIndexByUid(upUid);

        mjmlStore.duplicateSection({
          sectionIndex: sectionIndex,
          inVariant: true,
        });

        mjmlStore.selectSection({
          sectionIndex: sectionIndex + 1,
        });

        const newSectionId = mjmlStore.getSelectedSectionByIndex(
          sectionIndex + 1
        ).uid;

        if (thisSectionVariants.elseVariantUid) {
          thisSectionVariants.sections.splice(
            thisSectionVariants.sections.length - 1,
            0,
            newSectionId
          );
        } else {
          thisSectionVariants.sections.push(newSectionId);
        }

        thisSectionVariants.variants.push({
          [newSectionId]: {
            condition,
            name: langName,
          },
        });
        thisSectionVariants.activeVariant = newSectionId;

        this.updateElseCondition(thisSectionVariants);
      } else {
        mjmlStore.mjml.sectionVariants.push({
          sections: [id],
          variants: [
            {
              [id]: {
                condition,
                name: langName,
              },
            },
          ],
          activeVariant: id,
          elseCondition: "",
        });
      }
    },
    setEnableSectionVariants(
      enable: boolean,
      logicalOperatorsInsideVariant?: boolean,
      provider?: string,
      disableExtendedComparators?: boolean
    ) {
      this.enableSectionVariants = enable;
      if (
        logicalOperatorsInsideVariant === undefined &&
        provider === "sparkpost"
      ) {
        this.logicalOperatorsInsideVariant = true;
      } else if (logicalOperatorsInsideVariant !== undefined) {
        this.logicalOperatorsInsideVariant = logicalOperatorsInsideVariant;
      }
      if (disableExtendedComparators) {
        this.disableExtendedComparators = disableExtendedComparators;
      }
    },
    updateElseCondition(variants: TopolSectionVariants) {
      let elseCondition = "*|IF:!(";
      variants.variants.forEach((item, index) => {
        elseCondition +=
          `${index !== 0 ? ":OR:(" : "("}` +
          this.conditionBiulder(item[variants.sections[index]].condition) +
          ")";
      });
      elseCondition += ")|*";

      variants.elseCondition = elseCondition;
    },
    updateVariantCondition(
      id: string,
      conditionAttr: TopolVariantConditionKeys | "conditionsSeparator",
      value: string,
      skipSnapshot?: boolean
    ) {
      const thisSectionVariants = this.getThisVariant(id);
      const thisVariant = thisSectionVariants?.variants.find(
        (item: TopolVariant) => item[id]
      );

      if (thisSectionVariants && thisVariant && thisVariant[id]) {
        if (!skipSnapshot) {
          const mjmlStore = useMjmlStore();
          mjmlStore.takeSnapshot();
        }
        thisVariant[id].condition[conditionAttr] = value;

        this.updateElseCondition(thisSectionVariants);
      }
    },
    updateOtherCondition(
      id: string,
      index: number,
      conditionAttr: TopolVariantConditionKeys,
      value: string
    ) {
      const thisSectionVariants = this.getThisVariant(id);
      const thisVariant = thisSectionVariants?.variants.find(
        (item: TopolVariant) => item[id]
      );
      const otherConditions = thisVariant?.[id]?.condition.otherConditions;

      if (
        !thisSectionVariants ||
        !thisVariant ||
        !thisVariant[id] ||
        !otherConditions
      )
        return;

      const mjmlStore = useMjmlStore();
      mjmlStore.takeSnapshot();
      otherConditions[index][conditionAttr] = value;
      this.updateElseCondition(thisSectionVariants);
    },
    updateVariantName(id: string, name: string, skipSnapshot?: boolean) {
      const thisSectionVariants = this.getThisVariant(id);
      const thisVariant = thisSectionVariants?.variants.find(
        (item: TopolVariant) => item[id]
      );

      if (thisVariant && thisVariant[id]) {
        if (!skipSnapshot) {
          const mjmlStore = useMjmlStore();
          mjmlStore.takeSnapshot();
        }
        thisVariant[id].name = name;
      }
    },
    conditionLabel(condition: TopolVariantCondition): string {
      const mergeTagsStore = useMergeTagsStore();
      const start = mergeTagsStore.MTSyntax?.start ?? "*|";
      const end = mergeTagsStore.MTSyntax?.end ?? "|*";

      const comparatorWord = (comparator: string): string => {
        const map: Record<string, string> = {
          is: "variants.is",
          "is not": "variants.is_not",
          exist: "variants.exist",
          ">": "variants.greater_than",
          "<": "variants.less_than",
          ">=": "variants.greater_than_or_equal",
          "<=": "variants.less_than_or_equal",
        };
        return map[comparator] ? i18n.global.t(map[comparator]) : comparator;
      };

      const part = (field: string, comparator: string, value: string) => {
        const mergeTag = `${start}${field}${end}`;
        if (comparator === "exist") {
          return `${mergeTag} ${comparatorWord(comparator)}`;
        }
        return `${mergeTag} ${comparatorWord(comparator)} ${value}`;
      };

      let label = part(
        condition.conditionField,
        condition.conditionComparator,
        condition.conditionValue
      );

      if (condition.conditionsSeparator && condition.otherConditions) {
        const separatorWord = i18n.global.t(
          condition.conditionsSeparator === "AND"
            ? "variants.and"
            : "variants.or"
        );
        condition.otherConditions.forEach((other) => {
          label += ` ${separatorWord} ${part(
            other.conditionField,
            other.conditionComparator,
            other.conditionValue
          )}`;
        });
      }

      return label;
    },
    getCopyableConditions(targetId: string): {
      signature: string;
      label: string;
      condition: TopolVariantCondition;
    }[] {
      const mjmlStore = useMjmlStore();
      const groups = mjmlStore.mjml.sectionVariants;
      if (!groups) return [];

      const isFilled = (condition: TopolVariantCondition) => {
        if (condition.conditionField === "") return false;
        if (
          condition.conditionComparator !== "exist" &&
          condition.conditionValue === ""
        ) {
          return false;
        }
        return true;
      };

      const ownGroup = this.getThisVariant(targetId);
      const excluded = new Set<string>();
      if (ownGroup) {
        ownGroup.variants.forEach((variant: TopolVariant, index: number) => {
          const uid = ownGroup.sections[index];
          const condition = variant[uid]?.condition;
          if (condition && isFilled(condition)) {
            excluded.add(this.conditionBiulder(condition));
          }
        });
      }

      const result: {
        signature: string;
        label: string;
        condition: TopolVariantCondition;
      }[] = [];
      const seen = new Set<string>();

      groups.forEach((group) => {
        group.variants.forEach((variant: TopolVariant, index: number) => {
          const uid = group.sections[index];
          const entry = variant[uid];
          if (!entry || !isFilled(entry.condition)) return;

          const signature = this.conditionBiulder(entry.condition);
          if (!signature || excluded.has(signature) || seen.has(signature)) {
            return;
          }
          seen.add(signature);

          const conditionLabel = this.conditionLabel(entry.condition);
          result.push({
            signature,
            label: entry.name
              ? `${entry.name}: ${conditionLabel}`
              : conditionLabel,
            condition: entry.condition,
          });
        });
      });

      return result;
    },
    applyConditionToVariant(
      targetId: string,
      condition: TopolVariantCondition
    ) {
      const thisSectionVariants = this.getThisVariant(targetId);
      const thisVariant = thisSectionVariants?.variants.find(
        (item: TopolVariant) => item[targetId]
      );

      if (thisSectionVariants && thisVariant && thisVariant[targetId]) {
        const mjmlStore = useMjmlStore();
        mjmlStore.takeSnapshot();
        // Deep clone so the two variants never share a condition reference.
        thisVariant[targetId].condition = JSON.parse(JSON.stringify(condition));
        this.updateElseCondition(thisSectionVariants);
      }
    },
  },
});
